export function memberStatusLabel(status) {
  const labels = {
    active: "Active",
    pending: "Pending review",
    inactive: "Not approved",
    suspended: "Suspended",
    expired: "Expired",
    rejected: "Not approved",
    archived: "Archived"
  };
  return labels[status] || status || "Pending signup";
}

const LIBERTY_LONG_RANGE_QUALIFICATION_LABEL = "1,000-yard qualified";

export const RELEASE_FORM_ACTION_LABEL = "Review and sign release form";

export function memberPortalActionSettings(range) {
  const actions = range?.branding?.memberPortal?.actions || {};
  return {
    membershipStatus: actions.membershipStatus !== false,
    waiver: actions.waiver !== false,
    onboarding: actions.onboarding !== false,
    qualifications: actions.qualifications !== false,
    billing: actions.billing !== false,
    reservations: actions.reservations !== false,
    events: actions.events !== false,
    guestPasses: actions.guestPasses !== false,
    rentals: Boolean(actions.rentals)
  };
}

export function memberPortalTabFromHash(hash = "") {
  const normalized = String(hash || "").replace(/^#/, "").toLowerCase();
  if (["member-status", "member-membership", "membership", "onboarding"].includes(normalized)) {
    return "membership";
  }
  if (["member-visits", "visits", "reservations", "bookings"].includes(normalized)) {
    return "visits";
  }
  if (["events", "member-events", "classes", "member-classes"].includes(normalized)) {
    return "classes";
  }
  if (["member-payments", "payments", "receipts", "billing"].includes(normalized)) {
    return "payments";
  }
  if (["member-documents", "documents", "release-form", "rules", "materials"].includes(normalized)) {
    return "documents";
  }
  if (["member-support", "support", "help", "contact", "account"].includes(normalized)) {
    return "support";
  }
  return "home";
}

export function isLongRangeQualificationKey(value = "") {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return (
    normalized.includes("liberty_1000_yard_qualified") ||
    normalized.includes("1000") ||
    normalized.includes("1,000") ||
    normalized.includes("long range") ||
    normalized.includes("long-range")
  );
}

export function qualificationDisplayLabel(value = "") {
  const cleaned = String(value || "").trim();
  if (!cleaned) {
    return "Qualification";
  }
  if (isLongRangeQualificationKey(cleaned)) {
    return LIBERTY_LONG_RANGE_QUALIFICATION_LABEL;
  }
  return cleaned
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

export function requirementText(requirement = {}, fallback) {
  if (!requirement.required) {
    return "Not required";
  }
  return requirement.instructions || fallback;
}

export function backgroundCheckStatusCopy(requirement = {}) {
  if (requirement.required === false || !requirement.required) {
    return {
      label: "Not required",
      detail: "No background check requirement is active for this membership."
    };
  }
  if (requirement.status === "completed" || requirement.backgroundCheckDecision?.status === "clear") {
    return {
      label: "Clear",
      detail: "The range has recorded this requirement as complete."
    };
  }
  if (requirement.backgroundCheckDecision?.status === "failed") {
    return {
      label: "Review needed",
      detail: "Contact the range for next steps. Sensitive details are not shown here."
    };
  }
  if (requirement.backgroundCheckDecision?.status === "needs_info") {
    return {
      label: "More info needed",
      detail: "The range may contact you for additional information."
    };
  }
  if (requirement.provider === "checkr" || requirement.checkr) {
    const status = String(requirement.checkr?.status || "").toLowerCase();
    if (status === "submitted" || status === "in_progress") {
      return {
        label: "Submitted",
        detail: requirement.checkr?.eta
          ? `Screening is in progress. Estimated completion ${new Date(requirement.checkr.eta).toLocaleDateString()}.`
          : "Screening is in progress."
      };
    }
    if (status === "ready_for_owner_review") {
      return {
        label: "Ready for review",
        detail: "Screening is ready. The range will make the final membership decision."
      };
    }
    if (status === "needs_member_action") {
      return {
        label: "Action needed",
        detail: "Open this step and submit the requested information."
      };
    }
    if (status === "provider_failed") {
      return {
        label: "Needs attention",
        detail: "The screening provider could not process the check. Contact the range or resubmit if asked."
      };
    }
    return {
      label: "Not started",
      detail: "Submit the background-check form to continue membership review."
    };
  }
  return {
    label: "Pending",
    detail: requirement.instructions || "The range will update this after their background-check process."
  };
}

export function memberReviewFollowUpCopy(member = {}) {
  if (!member) {
    return null;
  }
  const review = member.review || {};
  const prequalificationDecision = review.requirements?.prequalification?.decision || {};
  const backgroundCheckDecision = review.requirements?.backgroundCheck?.backgroundCheckDecision || {};
  const decisionReason = review.reason ||
    prequalificationDecision.reason ||
    backgroundCheckDecision.reason ||
    member.reviewSummary?.prequalificationDecisionReason ||
    member.reviewSummary?.backgroundCheckDecisionReason ||
    "";

  if (member.status === "inactive" || review.status === "rejected") {
    return {
      label: "Not approved",
      title: "Membership not approved",
      body: decisionReason || "The range did not approve this membership signup. Contact the range for next steps.",
      actionTitle: "Contact the range",
      actionBody: "The range review is closed unless the range reopens your record."
    };
  }

  if (
    review.status === "needs_info" ||
    prequalificationDecision.status === "needs_info" ||
    backgroundCheckDecision.status === "needs_info" ||
    member.reviewSummary?.prequalificationDecisionStatus === "needs_info" ||
    member.reviewSummary?.backgroundCheckDecisionStatus === "needs_info"
  ) {
    return {
      label: "More info needed",
      title: "Range needs more information",
      body: decisionReason || "The range needs a follow-up response before it can finish your membership review.",
      actionTitle: "Respond to range follow-up",
      actionBody: "Review the onboarding checklist and update any requested information for staff review."
    };
  }

  if (prequalificationDecision.status === "rejected" || member.reviewSummary?.prequalificationDecisionStatus === "rejected") {
    return {
      label: "Qualification not approved",
      title: "Qualification needs follow-up",
      body: decisionReason || "The range did not approve the qualification step yet. You can contact the range or submit updated context.",
      actionTitle: "Review qualification follow-up",
      actionBody: "Check the onboarding and qualification steps for the reason recorded by the range."
    };
  }

  return null;
}

export function waiverDocumentName(waiver = {}) {
  return waiver.document?.versionLabel || waiver.document?.fileName || "Current release form";
}

export function waiverExpirationRule(waiver = {}) {
  const expiresInDays = Number.parseInt(waiver.expiresInDays || 0, 10);
  if (!Number.isFinite(expiresInDays) || expiresInDays <= 0) {
    return "No automatic re-sign schedule";
  }
  if (expiresInDays === 365) {
    return "Re-sign every year";
  }
  if (expiresInDays % 365 === 0) {
    return `Re-sign every ${expiresInDays / 365} years`;
  }
  if (expiresInDays % 30 === 0) {
    return `Re-sign every ${expiresInDays / 30} months`;
  }
  return `Re-sign every ${expiresInDays} days`;
}

export function waiverIsRequired(waiver = {}) {
  return waiver.required !== false;
}

export function waiverIsExpired(waiver = {}) {
  if (!waiver.applicantAcknowledgementExpiresAt) {
    return false;
  }
  const expiresAt = Date.parse(waiver.applicantAcknowledgementExpiresAt);
  return Number.isFinite(expiresAt) && expiresAt <= Date.now();
}

export function waiverRequiresSignature(waiver = {}) {
  return waiverIsRequired(waiver) && (waiver.status !== "completed" || waiverIsExpired(waiver));
}

export function waiverStatusCopy(waiver = {}) {
  if (!waiverIsRequired(waiver)) {
    return {
      label: "Not required",
      detail: "No release form is required for this membership.",
      signature: "Not required",
      actionLabel: "",
      expired: false
    };
  }
  if (waiverIsExpired(waiver)) {
    const expiresAt = new Date(waiver.applicantAcknowledgementExpiresAt).toLocaleDateString([], {
      dateStyle: "medium",
      timeZone: "UTC"
    });
    return {
      label: "Expired",
      detail: `Your prior release form expired ${expiresAt}. Re-sign the current version before access stays current.`,
      signature: `Expired ${expiresAt}`,
      actionLabel: RELEASE_FORM_ACTION_LABEL,
      expired: true
    };
  }
  if (waiver.status !== "completed") {
    return {
      label: "Signature required",
      detail: requirementText(waiver, "Review and acknowledge the current range release form before access is active."),
      signature: "Signature required",
      actionLabel: RELEASE_FORM_ACTION_LABEL,
      expired: false
    };
  }
  const acceptedAt = waiver.applicantAcknowledgedAt
    ? new Date(waiver.applicantAcknowledgedAt).toLocaleDateString([], {
        dateStyle: "medium",
        timeZone: "UTC"
      })
    : "Signed";
  const expiresAt = waiver.applicantAcknowledgementExpiresAt
    ? ` / expires ${new Date(waiver.applicantAcknowledgementExpiresAt).toLocaleDateString([], {
        dateStyle: "medium",
        timeZone: "UTC"
      })}`
    : "";
  return {
    label: "Signed",
    detail: waiver.applicantAcknowledgementExpiresAt
      ? `Signed ${acceptedAt}; expires ${new Date(waiver.applicantAcknowledgementExpiresAt).toLocaleDateString([], {
          dateStyle: "medium",
          timeZone: "UTC"
        })}.`
      : `Signed ${acceptedAt}.`,
    signature: `${acceptedAt}${expiresAt}`,
    actionLabel: "",
    expired: false
  };
}

export function waiverSignatureLabel(waiver = {}) {
  return waiverStatusCopy(waiver).signature;
}

export function memberDocumentTypeLabel(type = "") {
  const labels = {
    waiver: "Release form",
    id: "Identity document",
    prequalification: "Qualification support",
    background_check: "Background check support",
    other: "Member file"
  };
  return labels[type] || qualificationDisplayLabel(type || "Member file");
}

export function memberDocumentStatusLabel(status = "") {
  const labels = {
    approved: "Accepted",
    completed: "Accepted",
    current: "Current",
    pending: "Submitted",
    needs_review: "In review",
    rejected: "Needs update",
    expired: "Expired"
  };
  return labels[status] || (status ? qualificationDisplayLabel(status) : "On file");
}
